import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t as translate } from "@chuzi/shared/config";
import { ChuziApiError } from "@chuzi/shared/api";
import { auth } from "../lib/auth";
import { consumeOidcVerifier, oidcRedirectUri } from "../lib/oidc";
import { applyPendingSignupIfNeeded } from "../lib/pending-signup";

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState(
    translate(null, "auth_oidc_callback_processing", "Completing sign-in..."),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (!code || !state) {
      setError(
        translate(
          null,
          "auth_oidc_callback_failed",
          "Sign-in callback was missing required parameters.",
        ),
      );
      return;
    }

    async function run(authCode: string, authState: string) {
      try {
        const codeVerifier = consumeOidcVerifier(authState);
        const body = await exchangeOidcCode({
          code: authCode,
          code_verifier: codeVerifier,
          redirect_uri: oidcRedirectUri(),
          device_name: "chuzi-web",
        });
        if (!body.token) {
          throw new Error(
            translate(null, "auth_oidc_token_missing", "Sign-in token was missing."),
          );
        }
        auth.setToken(body.token);
        await applyPendingSignupIfNeeded(body.user.email);
        setMessage(translate(null, "auth_oidc_verified", "Signed in. Redirecting..."));
        navigate("/my", { replace: true });
      } catch (err) {
        if (err instanceof ChuziApiError) {
          const body = err.body as { message?: string; errors?: Record<string, string[]> };
          const first = body?.errors ? Object.values(body.errors)[0]?.[0] : null;
          setError(
            first ??
              body?.message ??
              translate(null, "auth_oidc_callback_failed", "Could not complete sign-in."),
          );
          return;
        }
        setError(
          err instanceof Error
            ? err.message
            : translate(null, "auth_oidc_callback_failed", "Could not complete sign-in."),
        );
      }
    }

    void run(code, state);
  }, [navigate, searchParams]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        background: "#04070d",
        color: "#e8f0ff",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 16,
          border: "1px solid rgba(126,184,255,0.45)",
          background: "rgba(8,14,28,0.75)",
          padding: "28px 24px",
          fontWeight: 700,
          fontSize: 18,
        }}
      >
        <p style={{ margin: 0 }}>{error ?? message}</p>
      </div>
    </div>
  );
}

type OidcExchangeBody = {
  code: string;
  code_verifier: string;
  redirect_uri: string;
  device_name: string;
};

async function exchangeOidcCode(payload: OidcExchangeBody): Promise<OidcExchangeResponse> {
  const baseUrl = (import.meta.env.VITE_CHUZI_API_URL as string | undefined)?.trim() ?? "";
  const response = await fetch(`${baseUrl}/api/v1/auth/oidc/exchange`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Chuzi-Client": "chuzi-web",
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    throw new ChuziApiError(response.status, parsed);
  }

  return parsed as OidcExchangeResponse;
}

type OidcExchangeResponse = {
  token?: string;
  user: {
    email: string;
  };
};
